import {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import useAsyncEffect from 'use-async-effect';
import EventsService from '@services/EventsService';
import ParticipantsService from '@services/ParticipantsService';

import {
  IMessage,
  MessageType,
  EAudioPlaybackType,
  SCREENS,
  AWAITING_RESPONSE_TYPE,
} from '@core/assistant/interfaces';
import {
  assistantSize,
  bedrockResultNotFoundReply,
  debugEventId,
  debugTagName,
  debugMapName,
  enableScriptAudioPreload,
  PRELOAD_SCRIPTS_CAP,
  bedrockResultNotFoundUserFriendlyReply,
} from '@core/assistant/constants';

import { IAssistantContext, ProviderProps } from './interfaces';
import { visualizeAudio } from './AudioVisualizer';
import { shared } from './Shared';
import { log, initDebugSendFn } from './Debug';

import {
  LinearScriptPlayer,
  ScriptNode,
} from './scriptPlayer/linearScriptPlayer';
import { ScriptNodeJSON } from './scriptPlayer/intefraces';
import AssistantScriptsService from '@services/AssistantScriptService';
import {
  IAssistantScript,
  PlaybackPolicy,
  ProgressEntry,
} from '@interfaces/assistantScript';
import AssistantQueryService from '@services/AssistantQueryService';
import { regionNameToCamelCase, formatSpotRegionName } from './Regions';
import { IEventSettingsAiAssistant } from '@interfaces/event';
import {
  PollyEngine,
  PollyLanguage,
  PollyVoice,
  selectVoiceForLanguage,
} from '@constants/polly';
import { teleportToRegion as teleportToRegionFn } from './providerV1';

export const AssistantContext = createContext<IAssistantContext>(
  {} as IAssistantContext,
);

export const useAssistant = () => useContext(AssistantContext);

let visualizerInstance: any;
// TODO: temp workaround, do a proper fix on SM side to have an option to use Transcibe only
let activeScreenGlob = SCREENS.DEFAULT;
// TODO: temp workaround
let isMutedGlob = false;
let currentRegion = '';

const defaultAvailableLanguages: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.FRENCH,
  PollyLanguage.ITALIAN,
  PollyLanguage.GERMAN,
  PollyLanguage.ARABIC,
  PollyLanguage.CHINESE,
  PollyLanguage.SPANISH,
];

const defaultAiSettings: IEventSettingsAiAssistant = {
  engine: 'generative',
  voiceId: 'Ruth',
  language: PollyLanguage.ENGLISH,
  useProfileLanguage: false,
};

const getCurrentRegion = () => {
  let currentArea = currentRegion;

  if (!currentRegion && debugTagName) {
    currentArea = debugTagName;
  }

  return currentArea;
};

const isLandingTrigger = (scriptTrigger: string | string[]): boolean => {
  const triggers = Array.isArray(scriptTrigger)
    ? scriptTrigger
    : [scriptTrigger];
  return triggers.some((t) => t.toLowerCase() === 'landing');
};

const isAITrigger = (scriptTrigger: string | string[]): boolean => {
  const triggers = Array.isArray(scriptTrigger)
    ? scriptTrigger
    : [scriptTrigger];
  return triggers.some((t) => t.startsWith('AITrigger_'));
};

const replaceAssistantPlaceholders = (
  text: string,
  userName?: string,
  experienceName?: string,
) =>
  text
    .replace(/\{\{user\}\}/g, userName?.trim() ?? '')
    .replace(/\{\{experience\}\}/g, experienceName?.trim() ?? '');

const AssistantProviderInner: FC<ProviderProps> = ({
  children,
  currentRegionName,
  userName,
  mytaverseEventRef,
  sendWSRef,
  selectedLanguageRef,
  mytaverseUserRef,
  objectPoiId,
  assistantScriptUpdateTick,
  activeBrandingId,
}) => {
  const [open, setOpen] = useState(false);
  const [audioLanguage, setAudioLanguage] = useState<PollyLanguage>(
    (selectedLanguageRef.current?.toLowerCase() as PollyLanguage) ||
      PollyLanguage.ENGLISH,
  );
  const audioLanguageRef = useRef(audioLanguage);
  audioLanguageRef.current = audioLanguage;
  const [simulatedRegionOverride, setSimulatedRegionOverride] = useState<
    string | undefined
  >(undefined);
  const [isAudioPlaybackActive, setIsAudioPlaybackActive] = useState(false);
  const [isPollyAudioLoading, setIsPollyAudioLoading] = useState(false);
  const [audioEndTick, setAudioEndTick] = useState(0);
  const [isScriptCompleted, setIsScriptCompleted] = useState(false);
  const [isScriptTransitioning, setIsScriptTransitioning] = useState(false);
  const [awaitingResponseType, setAwaitingResponseType] = useState(
    AWAITING_RESPONSE_TYPE.NONE,
  );
  const [activeScreen, setActiveScreen] = useState<SCREENS>(SCREENS.DEFAULT);
  const [navigationHistory, setNavigationHistory] = useState<SCREENS[]>([
    SCREENS.DEFAULT,
  ]);
  const [scripts, setScripts] = useState<IAssistantScript[]>([]);

  const [audioPromptHistory] = useState<string[]>([]);

  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);
  const [isDisplayUserFeedback, setIsDisplayUserFeedback] = useState(false);
  const [isScriptQuizActive, setIsScriptQuizActive] = useState(false);
  const [canUserSkip, setCanUserSkip] = useState(false);
  const [pendingScriptStart, setPendingScriptStart] = useState<{
    step: number;
    area: string;
    title: string;
  } | null>(null);
  const [lastUserMessageData, setLastUserMessageData] = useState({
    eventId: '',
    participantId: '',
    timestamp: 0,
  });

  const [chatHistory, setChatHistory] = useState<IMessage[]>([]);

  const progressState = useRef('{}');
  const saveProgressQueueRef = useRef<Promise<void>>(Promise.resolve());
  // mirrors progressState structure but in-memory only — resets each browser session
  const sessionProgressRef = useRef<Record<string, ProgressEntry[]>>({});

  // refs come from props — no direct hook calls to useMytaverse/useMytaverseEvent here
  const sendJSONMessageToWebSocketRef = sendWSRef;

  const navigationHistoryRef = useRef(navigationHistory);
  navigationHistoryRef.current = navigationHistory;

  const openRef = useRef(open);
  openRef.current = open;

  const scriptsRef = useRef(scripts);
  scriptsRef.current = scripts;

  const userNameRef = useRef(userName);
  userNameRef.current = userName;

  const linearScriptPlayerRef = useRef<LinearScriptPlayer>();
  const isAudioPlaybackActiveRef = useRef<boolean>(false);
  const audioPlaybackIdRef = useRef<number>(0);
  const enqueuedScriptIdRef = useRef<string | null>(null);
  const startFlowRef = useRef<(trigger: string, scriptId?: string) => void>(
    () => {},
  );
  const stopPlaybackRef = useRef<() => void>(() => {});
  const debugEventAiSettingsRef = useRef<IEventSettingsAiAssistant | null>(
    null,
  );
  const getEffectiveAiSettings = (): {
    engine: string;
    voiceId: string;
    language: PollyLanguage;
    baseLanguage: PollyLanguage;
    languages: PollyLanguage[] | undefined;
    useProfileLanguage: boolean;
    voicesByLanguage?: IEventSettingsAiAssistant['voicesByLanguage'];
  } => {
    const mytaverseEvent = mytaverseEventRef.current;
    let eventAiSettings: IEventSettingsAiAssistant | undefined;

    if (mytaverseEvent?.currentEventId) {
      // Try currentEvent first, fall back to searching the events list
      eventAiSettings =
        mytaverseEvent.currentEvent?.settings?.aiAssistant ??
        mytaverseEvent.events?.find(
          (event: any) => event.eventId === mytaverseEvent.currentEventId,
        )?.settings?.aiAssistant;
    } else if (debugEventId) {
      eventAiSettings = debugEventAiSettingsRef.current ?? undefined;
    }

    const language = eventAiSettings?.language || defaultAiSettings.language;
    const baseLanguage = eventAiSettings?.baseLanguage || language;

    return {
      engine: eventAiSettings?.engine || defaultAiSettings.engine,
      voiceId: eventAiSettings?.voiceId || defaultAiSettings.voiceId,
      language,
      baseLanguage,
      languages: eventAiSettings?.languages,
      useProfileLanguage: eventAiSettings?.useProfileLanguage ?? false,
      voicesByLanguage: eventAiSettings?.voicesByLanguage,
    };
  };

  const preRenderedAudioRef = useRef<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >([]);

  const setIsMuted = useCallback(() => {
    setIsMutedLoc((prev) => {
      isMutedGlob = !prev;
      return !prev;
    });
  }, []);

  const navigate = useCallback((screen: SCREENS, navigateBack?: boolean) => {
    if (navigateBack) {
      const history = navigationHistoryRef.current;
      activeScreenGlob = history[history.length - 2];
      setActiveScreen(history[history.length - 2]);
    } else {
      activeScreenGlob = screen;
      setActiveScreen(screen);
    }

    setNavigationHistory((prevState) => {
      if (navigateBack) {
        prevState.pop();
        return [...prevState];
      }

      return [...prevState, screen];
    });
  }, []);

  const addMessage = useCallback(
    ({ text, type }: { text: string; type: MessageType }) => {
      setChatHistory((prevState) => {
        const brandingId = activeBrandingIdRef.current || undefined;
        return [
          ...prevState,
          { text, type, timestamp: Date.now(), brandingId },
        ];
      });
    },
    [],
  );

  const handleSendRequest = useCallback(
    async (text: string) => {
      addMessage({ text, type: MessageType.USER });
      setIsDisplayUserFeedback(false);
      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.SHORT);

      shared.audioPlayBackType = EAudioPlaybackType.CHAT;
      log('currentRegion', currentRegion);

      const currentArea = getCurrentRegion();

      log('useAudioZoneBasedContext currentRegion', currentArea);
      log(mytaverseEventRef.current?.currentEventId);

      const modelResponse = await AssistantQueryService.queryAssistant({
        text,
        tag: formatSpotRegionName(currentArea),
        eventId: mytaverseEventRef.current?.currentEventId || 'debug',
        includeScriptContext:
          mytaverseEventRef.current?.currentEventId || debugTagName
            ? true
            : false,
        mapName: mytaverseEventRef.current?.currentRoom?.map?.name || undefined,
        brandingId:
          mytaverseEventRef.current?.currentRoom?.activeBrandingId || undefined,
      });

      setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);

      if (modelResponse.text !== bedrockResultNotFoundReply) {
        generatePollyAudio(
          modelResponse.text,
          PollyEngine.GENERATIVE,
          PollyVoice.JOANNA,
          PollyLanguage.ENGLISH,
        );
        addMessage({ text: modelResponse.text, type: MessageType.ASSISTANT });
      }

      if (
        modelResponse.text === bedrockResultNotFoundReply &&
        modelResponse.lastUserMessageData
      ) {
        addMessage({
          text: bedrockResultNotFoundUserFriendlyReply,
          type: MessageType.ASSISTANT,
        });
        setIsDisplayUserFeedback(true);
        setLastUserMessageData(modelResponse.lastUserMessageData);
      }

      log('useAudioZoneBasedContext modelResponse', modelResponse);
    },
    [addMessage],
  );

  const handleSubmitUserFeedback = useCallback(
    async (isUserApproved: boolean) => {
      await AssistantQueryService.flagAssistantReply({
        eventId: lastUserMessageData.eventId,
        timestamp: lastUserMessageData.timestamp,
        isFeedbackRequired: isUserApproved ? 2 : 3,
      });
      setIsDisplayUserFeedback(false);

      if (isUserApproved) {
        addMessage({
          text: 'Your request is recorded',
          type: MessageType.ASSISTANT,
        });
      }
    },
    [lastUserMessageData, addMessage],
  );

  function audioEndCallback(playbackId: number) {
    log('[audio] audioEndCallback fired', {
      playbackId,
      current: audioPlaybackIdRef.current,
    });

    if (playbackId !== audioPlaybackIdRef.current) {
      log(
        '[audio] audioEndCallback STALE — ignoring (stopped manually before natural end)',
      );
      return;
    }
    log('[audio] audioEndCallback NATURAL END');
    isAudioPlaybackActiveRef.current = false;
    linearScriptPlayerRef.current?.notifyAudioEnded();

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();

    if (
      linearScriptPlayerState?.isRunning &&
      (!linearScriptPlayerState?.currentNode.advanceOnAudioEnd ||
        linearScriptPlayerState?.currentNode.awaitButtonNext)
    ) {
      setIsAudioPlaybackActive(false);
      setAudioEndTick((t) => t + 1);
    }

    if (
      linearScriptPlayerState?.isRunning &&
      linearScriptPlayerState?.currentNode.advanceOnAudioEnd &&
      !linearScriptPlayerState?.currentNode.awaitButtonNext &&
      !enqueuedScriptIdRef.current
    ) {
      log('auto advance script');
      linearScriptPlayerRef.current?.triggerNext();
      log(
        'script current state',
        linearScriptPlayerRef.current?.getCurrentState(),
      );
    }

    log(
      'current node',
      linearScriptPlayerRef.current?.getCurrentState().currentNode.id,
    );

    // finish_step: if a script was enqueued while the current step was playing,
    // now that audio has ended naturally, stop the current player and start it
    if (enqueuedScriptIdRef.current) {
      const scriptId = enqueuedScriptIdRef.current;
      enqueuedScriptIdRef.current = null;

      const enqueuedScriptData = scriptsRef.current.find(
        (x) => x.scriptId === scriptId,
      );
      log(
        '[script] finish_step: audio ended naturally, starting enqueued script',
        {
          scriptId,
          playbackPolicy: enqueuedScriptData?.playbackPolicy,
          triggerType: enqueuedScriptData?.triggerType,
          interruptMode: enqueuedScriptData?.interruptMode,
        },
      );

      setIsScriptCompleted(false);
      setPendingScriptStart(null);
      setIsScriptTransitioning(true);

      // small timeout to avoid UI reflow quirks
      setTimeout(() => {
        if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
          linearScriptPlayerRef.current?.stop();
        }

        stopPlaybackRef.current();

        setIsScriptTransitioning(false);
        startFlowRef.current('', scriptId);
      }, 500);
    }
  }

  const stopPlayback = useCallback(() => {
    log('[audio] stopPlayback', {
      nextPlaybackId: audioPlaybackIdRef.current + 1,
    });
    log('[assistant] stopPlayback caller');
    audioPlaybackIdRef.current++;
    isAudioPlaybackActiveRef.current = false;
    setIsAudioPlaybackActive(false);

    try {
      visualizerInstance.stopAudio();
    } catch (_e) {
      // visualizerInstance may not be initialized yet
    }
  }, []);
  stopPlaybackRef.current = stopPlayback;

  const teleportToRegion = useCallback(
    (regionName: string) =>
      teleportToRegionFn(regionName, sendJSONMessageToWebSocketRef),
    [],
  );

  function onGenerateAudio(data: any) {
    log('activeScreen', activeScreenGlob);
    log('isMutedGlob', isMutedGlob);

    if (/*activeScreenGlob !== SCREENS.DEFAULT &&*/ !isMutedGlob) {
      log('onGenerateAudio data len', data.length);
      isAudioPlaybackActiveRef.current = true;
      log('isAudioPlaybackActiveRef', isAudioPlaybackActiveRef);
      setIsAudioPlaybackActive(true);
      const capturedPlaybackId = audioPlaybackIdRef.current;

      // important, check if audio is playing before stopping to avoid audio glitches
      if (visualizerInstance && visualizerInstance.playing) {
        log(
          '[audio] onGenerateAudio stopping previous visualizer before starting new one',
        );
        visualizerInstance.stopAudio();
      }

      log('[audio] onGenerateAudio START', { playbackId: capturedPlaybackId });
      visualizerInstance = visualizeAudio(
        assistantSize.width,
        assistantSize.height,
        () => audioEndCallback(capturedPlaybackId),
      );
      visualizerInstance.initialize(data);
    }
  }

  const generatePollyAudio = async (
    text: string,
    engine?: string,
    voiceId?: string,
    language?: string,
    disableOptimizations = true,
  ) => {
    log('generatePollyAudio muted', isMutedGlob);
    if (isMutedGlob) {
      isAudioPlaybackActiveRef.current = false;
      setIsAudioPlaybackActive(false);
      setAudioEndTick((t) => t + 1);
      linearScriptPlayerRef.current?.notifyAudioEnded();

      const mutedPlayerState = linearScriptPlayerRef.current?.getCurrentState();

      if (
        mutedPlayerState?.isRunning &&
        mutedPlayerState?.currentNode.advanceOnAudioEnd &&
        !mutedPlayerState?.currentNode.awaitButtonNext
      ) {
        linearScriptPlayerRef.current?.triggerNext();
      }

      return;
    }

    if (isAudioPlaybackActiveRef.current && visualizerInstance) {
      log('[audio] generatePollyAudio stopping previous audio for new request');
      visualizerInstance.stopAudio();
    }
    isAudioPlaybackActiveRef.current = false;

    const eff = getEffectiveAiSettings();
    const resolvedEngine = engine ?? eff.engine;
    const resolvedVoiceId = voiceId ?? eff.voiceId;
    const resolvedLanguage =
      (language as PollyLanguage | undefined) ?? eff.language;

    const existingAudio = preRenderedAudioRef.current.find(
      (x) =>
        x.text === text &&
        x.engine === resolvedEngine &&
        x.voiceId === resolvedVoiceId &&
        x.language === resolvedLanguage,
    );

    log('existingAudio', existingAudio);

    if (existingAudio?.audio) {
      log('prerendered audio exists', existingAudio);
      log(
        `Polly audio size before playback: ${(
          existingAudio.audio.byteLength / 1024
        ).toFixed(2)} KB`,
      );
      onGenerateAudio(existingAudio.audio);
      return;
    }

    let arrayBuffer: ArrayBuffer | null = null;

    setIsPollyAudioLoading(true);

    try {
      arrayBuffer = await AssistantQueryService.generateAssistantAudio({
        text,
        disableOptimizations,
        engine: resolvedEngine,
        voiceId: resolvedVoiceId,
        language: resolvedLanguage,
      });
    } catch (e) {
      log('Error: failed to fetch Polly audio', e);
      setIsPollyAudioLoading(false);
      return;
    }

    setIsPollyAudioLoading(false);

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      log('Error: empty or missing Polly audio response');
      return;
    }

    log(
      `Polly audio size before playback: ${(
        arrayBuffer.byteLength / 1024
      ).toFixed(2)} KB`,
    );

    if (existingAudio) {
      existingAudio.audio = arrayBuffer;
    }

    onGenerateAudio(arrayBuffer);
  };

  const playBlockAudio = useCallback(
    async (
      text: string,
      engine?: string,
      voiceId?: string,
      language?: string,
    ) => {
      log('playback block text ->', text);

      if (!openRef.current) {
        setOpen(true);

        setTimeout(() => {
          generatePollyAudio(text, engine, voiceId, language, false);
        }, 100);
        return;
      }
      generatePollyAudio(text, engine, voiceId, language, false);
    },
    [],
  );

  // Serialize all server saves through a queue so concurrent calls can't
  // arrive out of order and overwrite each other. Each enqueued save always
  // reads progressState.current at execution time, so it always sends the
  // latest accumulated state.
  const enqueueProgressSave = useCallback(() => {
    const stateAtEnqueue = progressState.current;
    log(
      '[progress] enqueueProgressSave called, state at enqueue:',
      stateAtEnqueue,
    );
    saveProgressQueueRef.current = saveProgressQueueRef.current.then(
      async () => {
        log(
          '[progress] queue item executing, state at execute time:',
          progressState.current,
        );
        if (mytaverseEventRef.current?.currentEventId || debugEventId) {
          await EventsService.setEventUserData(
            mytaverseEventRef.current?.currentEventId ?? debugEventId,
            { assistantState: progressState.current },
          );
          log('[progress] server save done, saved:', progressState.current);
        } else {
          log('[progress] SKIPPED server save — no eventId');
        }
      },
    );
  }, []);

  const cbOnCompletedStep = useCallback(
    (trigger: string, stepNumber: number, step: string) => {
      log(
        `[progress] cbOnCompletedStep called — trigger=${trigger} stepNumber=${stepNumber} step=${step} state BEFORE:`,
        progressState.current,
      );
      log(`${trigger} step ${step} completed`, {
        progressState: progressState.current,
        trigger,
      });

      const currentProgressState: Record<string, ProgressEntry[]> = JSON.parse(
        progressState.current || '{}',
      );

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      const existingIndex = currentProgressState[trigger].findIndex(
        (e) => e[0] === stepNumber,
      );
      if (existingIndex === -1) {
        currentProgressState[trigger].push([stepNumber, Date.now()]);
      } else {
        // update timestamp on replay so server always reflects the latest run
        currentProgressState[trigger][existingIndex][1] = Date.now();
      }

      progressState.current = JSON.stringify(currentProgressState);

      // mirror into session-only ref (same structure, never persisted)
      if (!sessionProgressRef.current[trigger]) {
        sessionProgressRef.current[trigger] = [];
      }
      const sessionExistingIndex = sessionProgressRef.current[
        trigger
      ].findIndex((e) => e[0] === stepNumber);
      if (sessionExistingIndex === -1) {
        sessionProgressRef.current[trigger].push([stepNumber, Date.now()]);
      } else {
        sessionProgressRef.current[trigger][sessionExistingIndex][1] =
          Date.now();
      }
      log(
        `[progress] cbOnCompletedStep state AFTER write:`,
        progressState.current,
      );

      log(
        'assistant progress saved',
        JSON.stringify(currentProgressState, null, 2),
      );

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const saveQuizAnswers = useCallback(
    async (selectedOptions: number[]) => {
      const player = linearScriptPlayerRef.current;
      const trigger = player?.getCurrentState().name;
      const nodeId = player?.getCurrentNodeId();

      log('saveQuizAnswers input', {
        trigger,
        nodeId,
        progressState: progressState.current,
      });

      if (!trigger || !nodeId) {
        log('saveQuizAnswers: no trigger or nodeId');
        return;
      }

      const stepNumber = parseInt(nodeId.replace('step-', ''), 10) - 1;
      if (isNaN(stepNumber)) {
        log('saveQuizAnswers: invalid stepNumber from nodeId', nodeId);
        return;
      }

      const currentProgressState: Record<string, ProgressEntry[]> = JSON.parse(
        progressState.current || '{}',
      );

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      const entries = currentProgressState[trigger];

      const entryIndex = entries.findIndex((e) => e[0] === stepNumber);

      if (entryIndex !== -1) {
        // cbOnCompletedStep ran first and entry already exists, add collected answers
        entries[entryIndex][2] = selectedOptions;
      } else {
        // cbOnCompletedStep hasn't run yet, create entry now with collected answers
        entries.push([stepNumber, Date.now(), selectedOptions]);
      }
      progressState.current = JSON.stringify(currentProgressState);

      log('quiz answers saved', JSON.stringify(currentProgressState, null, 2));

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const resetAssistantUI = useCallback(() => {
    activeScreenGlob = SCREENS.DEFAULT;
    setActiveScreen(SCREENS.DEFAULT);
    setNavigationHistory([SCREENS.DEFAULT]);
    setIsScriptCompleted(false);
    setIsScriptTransitioning(false);
    setIsScriptQuizActive(false);
    setIsDisplayUserFeedback(false);
    setCanUserSkip(false);
    setPendingScriptStart(null);
  }, []);

  const startFlow = useCallback(
    (scriptTrigger: string, scriptId?: string) => {
      setIsScriptCompleted(false);
      setIsDisplayUserFeedback(false);

      const currentScripts = scriptsRef.current;
      const script = scriptId
        ? currentScripts.find((x) => x.scriptId === scriptId)
        : currentScripts.find(
            (x) =>
              x.scriptTrigger === scriptTrigger ||
              x.scriptTrigger.includes(scriptTrigger),
          );
      setCanUserSkip(script?.canUserSkip ?? false);
      const isUiClick = script?.triggerType === 'ui_click';
      const scriptData = script?.scriptData;

      let parsedScriptData: ScriptNodeJSON[] = [];

      if (scriptData) {
        try {
          parsedScriptData = JSON.parse(scriptData);
          log('whole script', JSON.stringify(parsedScriptData, null, 2));
        } catch (error) {
          log('makePlayerFromScriptData error', error);
        }
      }

      if (!parsedScriptData.length) {
        return;
      }

      const currentArea = scriptTrigger ? scriptTrigger : getCurrentRegion();

      const playerState = linearScriptPlayerRef.current?.getCurrentState();
      if (playerState?.isRunning && playerState?.name === currentArea) {
        log('script player is already running');
        return;
      }

      const currentProgressState = JSON.parse(progressState.current || '{}');

      log('currentProgressState player', {
        currentProgressState,
        currentAreaProgress:
          currentProgressState?.[currentArea]?.at(-1)?.[0] ?? 0,
        currentAreaTotalSteps: parsedScriptData.length,
      });

      const defaultPlaybackPolicy = isAITrigger(script?.scriptTrigger ?? '')
        ? PlaybackPolicy.ALWAYS
        : PlaybackPolicy.ONCE_SESSION;
      const playbackPolicy = script?.playbackPolicy ?? defaultPlaybackPolicy;
      log('script playbackPolicy=', playbackPolicy);

      if (parsedScriptData.length > 0) {
        const lastStep = parsedScriptData.length - 1;
        const isCompletedIn = (state: Record<string, ProgressEntry[]>) =>
          !!state[currentArea] &&
          (state[currentArea]?.at(-1)?.[0] ?? 0) >= lastStep;

        let shouldSkip = false;
        if (playbackPolicy === PlaybackPolicy.ONCE_SESSION) {
          if (isCompletedIn(sessionProgressRef.current)) {
            log('script already played this session (once_session)');
            shouldSkip = true;
          }
        } else if (playbackPolicy === PlaybackPolicy.ONCE_USER) {
          if (isCompletedIn(currentProgressState)) {
            log('script already completed (once_user)');
            shouldSkip = true;
          }
        } else if (playbackPolicy === PlaybackPolicy.FIRST_TIME) {
          if (!mytaverseUserRef?.current?.isFirstTimeUser) {
            log('script skipped — not a first-time user (first_time)');
            shouldSkip = true;
          } else if (isCompletedIn(sessionProgressRef.current)) {
            log('script already played this session (first_time)');
            shouldSkip = true;
          }
        }

        // PlaybackPolicy.ALWAYS: no guard, always play
        if (shouldSkip) {
          if (!isAudioPlaybackActiveRef.current || isUiClick) {
            resetAssistantUI();
          }
          return;
        }
      }

      const effectiveAiSettings = getEffectiveAiSettings();

      const langKey = effectiveAiSettings.useProfileLanguage
        ? (selectedLanguageRef.current.toLowerCase() as PollyLanguage)
        : audioLanguageRef.current;

      // A language is eligible only when the event's languages list either is
      // absent (unrestricted) or explicitly includes this language.
      const allowedByEvent =
        !effectiveAiSettings.languages?.length ||
        effectiveAiSettings.languages.includes(langKey);

      // All steps must have a translation for the selected language; a single
      // missing step causes the entire script to fall back to baseLanguage.
      const allStepsTranslated =
        allowedByEvent &&
        parsedScriptData.every((node) => !!node.translations?.[langKey]);

      const missingTranslationSteps =
        allowedByEvent && !allStepsTranslated
          ? parsedScriptData
              .map((node, i) => (!node.translations?.[langKey] ? i + 1 : null))
              .filter(Boolean)
          : [];

      const allTranslationKeys = [
        ...new Set(
          parsedScriptData.flatMap((node) =>
            Object.keys(node.translations ?? {}),
          ),
        ),
      ];
      const fullyTranslatedLanguages = [
        effectiveAiSettings.baseLanguage,
        ...allTranslationKeys.filter((lang) =>
          parsedScriptData.every((node) => !!node.translations?.[lang]),
        ),
      ];

      const incompleteTranslations = allTranslationKeys
        .filter((lang) => !fullyTranslatedLanguages.includes(lang))
        .reduce<Record<string, number[]>>((acc, lang) => {
          acc[lang] = parsedScriptData
            .map((node, i) => (!node.translations?.[lang] ? i + 1 : null))
            .filter((n): n is number => n !== null);
          return acc;
        }, {});

      log('[lang] building nodes', {
        selectedLanguage: selectedLanguageRef.current,
        langKey,
        baseLanguage: effectiveAiSettings.baseLanguage,
        allowedLanguages: effectiveAiSettings.languages ?? 'unrestricted',
        allowedByEvent,
        allStepsTranslated,
        missingTranslationSteps,
        fullyTranslatedLanguages,
        incompleteTranslations,
        totalSteps: parsedScriptData.length,
        currentArea,
      });

      const nodes: ScriptNode[] = parsedScriptData.map((node, index) => {
        let advanceOnAudioEndFallback = false;
        const originalText = node.text;

        let nodeLanguage: PollyLanguage = effectiveAiSettings.baseLanguage;
        let nodeEngine = effectiveAiSettings.engine;
        let nodeVoiceId = effectiveAiSettings.voiceId;

        if (allStepsTranslated) {
          const selected = selectVoiceForLanguage(
            langKey,
            effectiveAiSettings.voicesByLanguage,
          );
          node.text = node.translations![langKey];
          nodeLanguage = langKey;
          nodeEngine = selected.engine;
          nodeVoiceId = selected.voiceId;
          log(`[lang] step-${index + 1}: using translation for "${langKey}"`, {
            originalText,
            translatedText: node.text,
            pollyLanguage: nodeLanguage,
            engine: nodeEngine,
            voiceId: nodeVoiceId,
          });
        } else {
          const reason = !allowedByEvent
            ? `"${langKey}" not in event languages list`
            : `script has untranslated steps (${missingTranslationSteps.join(
                ', ',
              )})`;
          const baseTranslation =
            node.translations?.[effectiveAiSettings.baseLanguage];
          if (baseTranslation !== undefined) {
            node.text = baseTranslation;
          }
          log(
            `[lang] step-${index + 1}: falling back to baseLanguage (${
              effectiveAiSettings.baseLanguage
            }) — ${reason}`,
            {
              originalText,
              pollyLanguage: nodeLanguage,
            },
          );
        }
        node.text = replaceAssistantPlaceholders(
          node.text,
          userNameRef.current,
          mytaverseEventRef.current.currentEvent?.name,
        );

        // force script completion on last step if no advance trigger is specified
        if (
          index === parsedScriptData.length - 1 &&
          !node.awaitButtonNext &&
          node.questionary?.type !== 'multiple_choice' &&
          !node.advanceOnAudioEnd &&
          !node.allowedKeys?.length &&
          !node.enableClickAnywhere
        ) {
          advanceOnAudioEndFallback = true;
        }

        if (
          !preRenderedAudioRef.current.find(
            (x) =>
              x.text === node.text &&
              x.engine === nodeEngine &&
              x.voiceId === nodeVoiceId &&
              x.language === nodeLanguage,
          )
        ) {
          preRenderedAudioRef.current.push({
            area: currentArea,
            index: index + 1,
            audio: null,
            text: node.text,
            engine: nodeEngine,
            voiceId: nodeVoiceId,
            language: nodeLanguage,
          });
        }

        return {
          id: `step-${index + 1}`,
          play: () => {
            cbOnCompletedStep(currentArea, index, `step-${index + 1}`);
            return playBlockAudio(
              node.text,
              nodeEngine,
              nodeVoiceId,
              nodeLanguage,
            );
          },
          awaitButtonNext:
            node.questionary?.type === 'multiple_choice'
              ? true
              : node.awaitButtonNext ?? false,
          awaitAudioEndBeforeInput: node.allowedKeys?.length ? true : false,
          advanceOnAudioEnd: advanceOnAudioEndFallback
            ? advanceOnAudioEndFallback
            : node.advanceOnAudioEnd,
          delayBeforeAdvancingMs: node.delayBeforeAdvancingMs ?? 0,
          enableClickAnywhere: node.enableClickAnywhere ?? false,
          allowedKeys: node.allowedKeys || [],
          cbOnCompletedStep: () => {},
          questionary: node.questionary,
          text: node.text,
          //cbOnCompletedStep(currentArea, index + 1, `step-${index + 1}`),
        };
      });

      nodes.push({
        id: `step-${nodes.length + 1}`,
        play: async () => {
          linearScriptPlayerRef.current?.complete();
          setIsScriptCompleted(true);

          /*cbOnCompletedStep(
          currentArea,
          parsedScriptData.length,
          `step-${parsedScriptData.length}`,
        );*/
        },
      });

      log('build nodes', nodes);

      log('preRenderedAudio ' + currentArea, preRenderedAudioRef.current);

      setIsScriptCompleted(false);
      linearScriptPlayerRef.current = new LinearScriptPlayer(nodes, {
        name: currentArea,
        onComplete: () => setIsScriptCompleted(true),
      });
      let step = 1;

      // resume from breakpoint only for ONCE_USER — other policies always start fresh
      if (
        playbackPolicy === PlaybackPolicy.ONCE_USER &&
        currentProgressState?.[currentArea]
      ) {
        step = (currentProgressState?.[currentArea]?.at(-1)?.[0] ?? -1) + 2;
      }

      log(
        `[progress] startFlow starting player at step-${step}, progressState:`,
        progressState.current,
      );

      if (isUiClick) {
        if (!openRef.current) {
          setOpen(true);
        }
        setPendingScriptStart({
          step,
          area: currentArea,
          title:
            script?.uiClickButtonLabel ||
            script?.startScriptButtonTitle ||
            'Take a tour',
        });
      } else {
        linearScriptPlayerRef.current.start(`step-${step}`);
        startAudioPrerender(currentArea, step);
      }

      //log('polly response', pollyResponse);
    },
    [resetAssistantUI],
  );
  startFlowRef.current = startFlow;

  const confirmScriptStart = useCallback(() => {
    if (!pendingScriptStart) {
      return;
    }
    const { step, area } = pendingScriptStart;
    setPendingScriptStart(null);
    linearScriptPlayerRef.current?.start(`step-${step}`);
    startAudioPrerender(area, step);
  }, [pendingScriptStart]);

  const startAudioPrerender = async (
    currentArea: string,
    startStep: number,
  ) => {
    const preRenderedAudioScript = preRenderedAudioRef.current.filter(
      (x) => x.area === currentArea && !x.audio,
    );
    log(
      'startAudioPrerender audio ' + currentArea,
      preRenderedAudioScript.length,
    );

    for (let i = startStep; i < preRenderedAudioScript.length; i++) {
      log('startAudioPrerender audio ' + i);

      try {
        const arrayBuffer = await AssistantQueryService.generateAssistantAudio({
          text: preRenderedAudioScript[i].text,
          disableOptimizations: true,
          engine: preRenderedAudioScript[i].engine,
          voiceId: preRenderedAudioScript[i].voiceId,
          language: preRenderedAudioScript[i].language,
        });

        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          log('Error: empty or missing Polly audio response for step ' + i);
          continue;
        }

        preRenderedAudioScript[i].audio = arrayBuffer;
        log(
          'startAudioPrerender audio ' + i,
          `${(arrayBuffer.byteLength / 1024).toFixed(2)} KB`,
        );
      } catch (e) {
        log('Error: failed to fetch Polly audio for step ' + i, e);
      }
    }

    log('preRenderedAudioScript', preRenderedAudioScript);
  };

  // TODO: deduplicate code
  const clearPrerenderedAudio = useCallback(() => {
    preRenderedAudioRef.current = [];
    log('prerendered audio cleared');
  }, []);

  const preloadScriptsAudio = useCallback(
    async (filteredScripts: IAssistantScript[]) => {
      if (!enableScriptAudioPreload) {
        return;
      }

      if (!mytaverseEventRef.current?.currentEventId && !debugEventId) {
        return;
      }

      const effectiveAiSettings = getEffectiveAiSettings();
      const langKey = effectiveAiSettings.useProfileLanguage
        ? (selectedLanguageRef.current.toLowerCase() as PollyLanguage)
        : audioLanguageRef.current;

      const landingScripts = filteredScripts.filter((s) =>
        isLandingTrigger(s.scriptTrigger),
      );
      const regionScripts = filteredScripts.filter(
        (s) =>
          !isLandingTrigger(s.scriptTrigger) &&
          !isAITrigger(s.scriptTrigger) &&
          s.triggerType !== 'object',
      );
      const aiTriggerScripts = filteredScripts.filter((s) =>
        isAITrigger(s.scriptTrigger),
      );
      const poiScripts = filteredScripts.filter(
        (s) => s.triggerType === 'object',
      );

      log('[preload] script counts', {
        landing: landingScripts.length,
        region: regionScripts.length,
        aiTrigger: aiTriggerScripts.length,
        poi: poiScripts.length,
      });

      let scriptsPreloaded = 0;

      const preloadScript = async (
        script: IAssistantScript,
        maxSteps: number | null,
      ) => {
        if (scriptsPreloaded >= PRELOAD_SCRIPTS_CAP) {
          return;
        }

        let parsedNodes: ScriptNodeJSON[];

        try {
          parsedNodes = JSON.parse(script.scriptData || '[]');
        } catch (error) {
          log('[preload] failed to parse script data', {
            scriptId: script.scriptId,
            error,
          });
          return;
        }

        if (!parsedNodes.length) {
          return;
        }

        const nodesToPreload =
          maxSteps !== null ? parsedNodes.slice(0, maxSteps) : parsedNodes;
        const area = Array.isArray(script.scriptTrigger)
          ? script.scriptTrigger[0]
          : script.scriptTrigger;

        const allowedByEvent =
          !effectiveAiSettings.languages?.length ||
          effectiveAiSettings.languages.includes(langKey);
        const allStepsTranslated =
          allowedByEvent &&
          parsedNodes.every((node) => !!node.translations?.[langKey]);

        scriptsPreloaded++;

        for (const [index, node] of nodesToPreload.entries()) {
          let text =
            allStepsTranslated && node.translations?.[langKey]
              ? node.translations[langKey]
              : node.text;
          let engine = effectiveAiSettings.engine;
          let voiceId = effectiveAiSettings.voiceId;
          let language: PollyLanguage = effectiveAiSettings.baseLanguage;

          if (allStepsTranslated) {
            const selected = selectVoiceForLanguage(
              langKey,
              effectiveAiSettings.voicesByLanguage,
            );
            language = langKey;
            engine = selected.engine;
            voiceId = selected.voiceId;
          }

          text = replaceAssistantPlaceholders(
            text,
            userNameRef.current,
            mytaverseEventRef.current.currentEvent?.name,
          );

          if (
            preRenderedAudioRef.current.find(
              (x) =>
                x.text === text &&
                x.engine === engine &&
                x.voiceId === voiceId &&
                x.language === language,
            )
          ) {
            continue;
          }

          preRenderedAudioRef.current.push({
            area,
            index: index + 1,
            audio: null,
            text,
            engine,
            voiceId,
            language,
          });

          try {
            const arrayBuffer =
              await AssistantQueryService.generateAssistantAudio({
                text,
                disableOptimizations: true,
                engine,
                voiceId,
                language,
              });

            if (!arrayBuffer || arrayBuffer.byteLength === 0) {
              log('[preload] empty audio response', {
                area,
                step: index + 1,
                scriptId: script.scriptId,
              });
              continue;
            }

            const entry = preRenderedAudioRef.current.find(
              (x) =>
                x.text === text &&
                x.engine === engine &&
                x.voiceId === voiceId &&
                x.language === language,
            );

            if (entry) {
              entry.audio = arrayBuffer;
            }

            log(
              `[preload] "${area}" step ${index + 1}: ${(
                arrayBuffer.byteLength / 1024
              ).toFixed(2)} KB`,
            );
          } catch (error) {
            log('[preload] failed to fetch audio', {
              area,
              step: index + 1,
              scriptId: script.scriptId,
              error,
            });
          }
        }
      };

      // Priority order: landing (full) → region (step 1) → ai_trigger (step 1) → poi (step 1)
      for (const script of landingScripts) {
        await preloadScript(script, null);
      }

      for (const script of regionScripts) {
        await preloadScript(script, 1);
      }

      for (const script of aiTriggerScripts) {
        await preloadScript(script, 1);
      }

      for (const script of poiScripts) {
        await preloadScript(script, 1);
      }

      log('[preload] completed', {
        scriptsPreloaded,
        totalCached: preRenderedAudioRef.current.length,
      });
    },
    [],
  );

  const fetchScripts = useCallback(async () => {
    const scripts = await AssistantScriptsService.getAssistantScripts();

    log('loaded assistant scripts', scripts);

    const currentMapName =
      mytaverseEventRef.current?.currentRoom?.map?.name || debugMapName;
    const currentBrandingId =
      mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
      activeBrandingIdRef.current;

    const filteredScripts = scripts.filter((s) => {
      if (currentMapName && s.mapName !== currentMapName) {
        return false;
      }

      if (currentBrandingId && s.brandingId !== currentBrandingId) {
        return false;
      }

      return true;
    });

    log('filtered assistant scripts', {
      currentMapName,
      currentBrandingId,
      count: filteredScripts.length,
    });

    if (!mytaverseEventRef.current?.currentEventId && debugEventId) {
      try {
        const eventParticipantData = await EventsService.getEvent(debugEventId);
        const aiAssistantSettings =
          eventParticipantData?.event?.settings?.aiAssistant;

        if (aiAssistantSettings) {
          debugEventAiSettingsRef.current = aiAssistantSettings;
          log('loaded debug event AI assistant settings', aiAssistantSettings);
        }
      } catch (error) {
        log('failed to load debug event AI assistant settings', error);
      }
    }

    setScripts(filteredScripts);
    void preloadScriptsAudio(filteredScripts);
  }, []);

  const mergeProgressStates = (
    current: string | null | undefined,
    stored: string | null | undefined,
  ): string => {
    const currentState: Record<string, ProgressEntry[]> = JSON.parse(
      current || '{}',
    );
    const storedState: Record<string, ProgressEntry[]> = JSON.parse(
      stored || '{}',
    );

    const merged = { ...storedState };
    for (const [key, entries] of Object.entries(currentState)) {
      if (!merged[key]) {
        merged[key] = entries;
      } else {
        // keep all entries from current that db doesn't have
        for (const entry of entries) {
          const exists = merged[key].some((e) => e[0] === entry[0]);
          if (!exists) {
            merged[key].push(entry);
          }
        }
        // sort by stepNumber ascending
        merged[key].sort((a, b) => a[0] - b[0]);
      }
    }
    return JSON.stringify(merged);
  };

  const fetchParticipantProgress = useCallback(async () => {
    log(
      '[progress] fetchParticipantProgress called, current state BEFORE:',
      progressState.current,
    );
    if (!mytaverseEventRef.current?.currentEventId) {
      // debug only
      const allParticipantEvents =
        await ParticipantsService.getCurrentParticipantEvents();
      const eventParticipantData = allParticipantEvents.events.find(
        (event) => event.eventId === debugEventId,
      );

      if (eventParticipantData) {
        log(
          '[progress] fetchParticipantProgress server assistantState:',
          eventParticipantData?.assistantState,
        );
        progressState.current = mergeProgressStates(
          progressState.current,
          eventParticipantData?.assistantState as string,
        );
      } else {
        log('no debug state data');
      }
    } else {
      log(
        '[progress] fetchParticipantProgress server assistantState:',
        mytaverseEventRef.current.currentEventParticipant?.assistantState,
      );
      progressState.current = mergeProgressStates(
        progressState.current,
        mytaverseEventRef.current.currentEventParticipant
          ?.assistantState as string,
      );
    }

    log(
      '[progress] fetchParticipantProgress state AFTER merge:',
      progressState.current,
    );
    log('loaded participant progress', progressState.current);
  }, []);

  const resetProgress = useCallback(async () => {
    log('[progress] resetProgress called');
    // Stop any running script and audio playback
    if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
      linearScriptPlayerRef.current.stop();
    }
    stopPlaybackRef.current();
    enqueuedScriptIdRef.current = null;
    // Reset all UI flags to their default state
    activeScreenGlob = SCREENS.DEFAULT;
    setActiveScreen(SCREENS.DEFAULT);
    setNavigationHistory([SCREENS.DEFAULT]);
    setIsScriptCompleted(false);
    setIsScriptTransitioning(false);
    setIsScriptQuizActive(false);
    setIsDisplayUserFeedback(false);
    setCanUserSkip(false);
    setPendingScriptStart(null);
    setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
    // Clear in-memory state immediately so any writes that happen while the
    // HTTP request is in-flight start from a clean slate.
    progressState.current = '{}';
    sessionProgressRef.current = {};
    // Make the reset save the new queue base so that any saves enqueued
    // after this are guaranteed to arrive at the server AFTER the reset.
    const resetSave = EventsService.setEventUserData(
      mytaverseEventRef.current?.currentEventId ?? debugEventId,
      { assistantState: null },
    ).catch(() => {});
    saveProgressQueueRef.current = resetSave;
    await resetSave;
    log('[progress] resetProgress done, state:', progressState.current);
  }, []);

  useEffect(() => {
    initDebugSendFn((msg) => sendJSONMessageToWebSocketRef.current(msg));
    return () => initDebugSendFn(() => {});
  }, []);

  useEffect(() => {
    if (objectPoiId === null) {
      resetAssistantUI();
      return;
    }

    if (!objectPoiId) {
      return;
    }

    const script = scriptsRef.current.find(
      (s) =>
        s.triggerType === 'object' &&
        (Array.isArray(s.scriptTrigger)
          ? s.scriptTrigger.includes(objectPoiId)
          : s.scriptTrigger === objectPoiId),
    );

    if (!script) {
      log('[object trigger] no script found for poiId', { objectPoiId });
      return;
    }

    log('[object trigger] starting script', {
      objectPoiId,
      scriptId: script.scriptId,
    });
    startFlowRef.current(objectPoiId, script.scriptId);
  }, [objectPoiId, resetAssistantUI]);

  if (!mytaverseEventRef.current) {
    mytaverseEventRef.current = {} as any;
  }

  const handleRegionChange = useCallback((incomingRegion: string) => {
    log('[region] changed to', incomingRegion);

    // Guard: same region + audio actively playing — avoid double playback on UE5 reconnect
    const playerState = linearScriptPlayerRef.current?.getCurrentState();
    if (
      playerState?.isRunning &&
      playerState.name === incomingRegion &&
      isAudioPlaybackActiveRef.current
    ) {
      log(
        '[region] script already running with active audio for this region — skipping restart',
      );
      return;
    }

    const scriptData = scriptsRef.current.find((x) => {
      const scriptTrigger = (
        Array.isArray(x.scriptTrigger) ? x.scriptTrigger : [x.scriptTrigger]
      ).map((trigger) =>
        trigger.includes('_') && !trigger.startsWith('AITrigger_')
          ? regionNameToCamelCase(trigger)
          : trigger,
      );
      return scriptTrigger.includes(incomingRegion as string);
    });

    log('[region] script found for region', {
      scriptId: scriptData?.scriptId ?? 'none',
      triggerType: scriptData?.triggerType,
      interruptMode: scriptData?.interruptMode,
      canUserSkip: scriptData?.canUserSkip,
    });

    // Guard: script already completed — if a different region's audio is playing, update region silently
    const defaultPlaybackPolicy = isAITrigger(scriptData?.scriptTrigger ?? '')
      ? PlaybackPolicy.ALWAYS
      : PlaybackPolicy.ONCE_SESSION;
    const effectivePlaybackPolicy =
      scriptData?.playbackPolicy ?? defaultPlaybackPolicy;

    if (scriptData) {
      if (!scriptData.interruptMode) {
        scriptData.interruptMode = 'finish_step';
      }

      const parsedNodes: ScriptNodeJSON[] = JSON.parse(
        scriptData.scriptData || '[]',
      );
      const lastNodeIndex = parsedNodes.length - 1;
      const isCompletedIn = (state: Record<string, ProgressEntry[]>) =>
        parsedNodes.length > 0 &&
        (state[incomingRegion]?.at(-1)?.[0] ?? 0) >= lastNodeIndex;

      const isCompleted =
        effectivePlaybackPolicy === PlaybackPolicy.ONCE_SESSION ||
        effectivePlaybackPolicy === PlaybackPolicy.FIRST_TIME
          ? isCompletedIn(sessionProgressRef.current)
          : isCompletedIn(JSON.parse(progressState.current || '{}'));

      if (isCompleted && effectivePlaybackPolicy !== PlaybackPolicy.ALWAYS) {
        const currentPlayerState =
          linearScriptPlayerRef.current?.getCurrentState();
        const protectingDifferentRegion =
          (currentPlayerState?.isRunning || isAudioPlaybackActiveRef.current) &&
          currentPlayerState?.name !== incomingRegion;
        if (protectingDifferentRegion) {
          log(
            '[region] script already completed — updating region silently, not stopping audio',
            incomingRegion,
          );
          currentRegion = incomingRegion;
          return;
        }
        log(
          '[region] script already completed — fall through to normal flow',
          incomingRegion,
        );
      }
    }

    // No script for this region — stop current player and audio
    if (!scriptData) {
      log(
        '[region] no script for region — stopping current player',
        incomingRegion,
      );

      const currentPlayerName =
        linearScriptPlayerRef.current?.getCurrentState().name;

      // Landing scripts are not region-based — a region clear (empty incomingRegion)
      // caused by the upstream debounce must not interrupt them.
      if (!incomingRegion && isLandingTrigger(currentPlayerName ?? '')) {
        currentRegion = incomingRegion;
        return;
      }

      const currentScript = currentPlayerName
        ? scriptsRef.current.find((x) => {
            const triggers = (
              Array.isArray(x.scriptTrigger)
                ? x.scriptTrigger
                : [x.scriptTrigger]
            ).map((t) =>
              t.includes('_') && !t.startsWith('AITrigger_')
                ? regionNameToCamelCase(t)
                : t,
            );
            return triggers.includes(currentPlayerName);
          })
        : null;

      const wasAudioPlaying = isAudioPlaybackActiveRef.current;

      if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
        linearScriptPlayerRef.current?.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      currentRegion = incomingRegion;

      if (
        !incomingRegion ||
        !wasAudioPlaying ||
        currentScript?.triggerType === 'ui_click'
      ) {
        log('[region] no script — resetting UI to default state');
        activeScreenGlob = SCREENS.DEFAULT;
        setActiveScreen(SCREENS.DEFAULT);
        setNavigationHistory([SCREENS.DEFAULT]);
        setIsScriptCompleted(false);
        setIsScriptTransitioning(false);
        setIsScriptQuizActive(false);
        setIsDisplayUserFeedback(false);
        setCanUserSkip(false);
        setPendingScriptStart(null);
      }
      return;
    }

    const { triggerType, interruptMode } = scriptData;
    const isPlayerRunning =
      linearScriptPlayerRef.current?.getCurrentState().isRunning ?? false;

    // Guard: check the currently running script's own interruptMode to decide
    // whether the incoming script can start or even be enqueued.
    if (isPlayerRunning) {
      const currentPlayerName =
        linearScriptPlayerRef.current?.getCurrentState().name;
      const currentScriptData = currentPlayerName
        ? scriptsRef.current.find((x) => {
            const triggers = (
              Array.isArray(x.scriptTrigger)
                ? x.scriptTrigger
                : [x.scriptTrigger]
            ).map((t) => (t.includes('_') ? regionNameToCamelCase(t) : t));
            return triggers.includes(currentPlayerName);
          })
        : null;

      if (currentScriptData?.interruptMode === 'non_interruptible') {
        log(
          '[region] non_interruptible (current script) — incoming script will not start or enqueue:',
          scriptData.scriptId,
        );
        currentRegion = incomingRegion;
        return;
      }

      if (
        currentScriptData?.interruptMode === 'finish_step' &&
        effectivePlaybackPolicy !== PlaybackPolicy.ALWAYS
      ) {
        // If audio already ended and player is waiting for user input,
        // trigger dequeue immediately instead of waiting for audioEndCallback
        if (!isAudioPlaybackActiveRef.current) {
          log(
            '[region] finish_step (current script) — audio already ended, starting enqueued script immediately',
            {
              scriptId: scriptData.scriptId,
              playbackPolicy: scriptData.playbackPolicy,
              triggerType: scriptData.triggerType,
              interruptMode: scriptData.interruptMode,
            },
          );
          const scriptId = scriptData.scriptId;
          setIsScriptCompleted(false);
          setPendingScriptStart(null);
          setIsScriptTransitioning(true);
          currentRegion = incomingRegion;
          setTimeout(() => {
            if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
              linearScriptPlayerRef.current?.stop();
            }
            stopPlaybackRef.current();
            setIsScriptTransitioning(false);
            startFlowRef.current('', scriptId);
          }, 500);
          return;
        }
        log(
          '[region] finish_step (current script) — enqueueing incoming script to start after current step ends',
          {
            scriptId: scriptData.scriptId,
            playbackPolicy: scriptData.playbackPolicy,
            triggerType: scriptData.triggerType,
            interruptMode: scriptData.interruptMode,
          },
        );
        enqueuedScriptIdRef.current = scriptData.scriptId;
        currentRegion = incomingRegion;
        return;
      }
    }

    // Case 1: ui_click — never auto-start, show Take a tour button
    if (triggerType === 'ui_click') {
      log(
        '[region] ui_click — stopping current script, setting pendingScriptStart',
        {
          scriptId: scriptData.scriptId,
          playbackPolicy: scriptData.playbackPolicy,
          triggerType,
          interruptMode,
        },
      );
      if (isPlayerRunning) {
        linearScriptPlayerRef.current?.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      currentRegion = incomingRegion;
      startFlowRef.current('', scriptData.scriptId);
      return;
    }

    // Case 3: non_interruptible — do not interrupt the currently running script
    if (interruptMode === 'non_interruptible' && isPlayerRunning) {
      log(
        '[region] non_interruptible — current script is running, not interrupting. New region script will not start:',
        scriptData.scriptId,
      );
      currentRegion = incomingRegion;
      return;
    }

    // Case 2: hard_cut (or area_entry with no running player) — stop immediately and start new script
    log('[region] hard_cut — stopping current script and starting new one', {
      scriptId: scriptData.scriptId,
      playbackPolicy: scriptData.playbackPolicy,
      triggerType,
      interruptMode,
    });
    if (isPlayerRunning) {
      linearScriptPlayerRef.current?.stop();
    }
    stopPlaybackRef.current();
    enqueuedScriptIdRef.current = null;
    currentRegion = incomingRegion;
    startFlowRef.current('', scriptData.scriptId);
  }, []);

  const simulateRegionChange = useCallback((regionName: string) => {
    setSimulatedRegionOverride(regionName);
  }, []);

  const effectiveRegionName = simulatedRegionOverride ?? currentRegionName;

  useEffect(() => {
    // Region clears are debounced upstream, so null here represents a real
    // leave event and should be passed through to the region handler.
    log('effective region name', effectiveRegionName);
    handleRegionChange(effectiveRegionName ?? '');
  }, [effectiveRegionName]);

  useAsyncEffect(async () => {
    await Promise.all([fetchScripts(), fetchParticipantProgress()]);
  }, []);

  useEffect(() => {
    if (!assistantScriptUpdateTick) {
      return;
    }

    clearPrerenderedAudio();
    fetchScripts();
  }, [assistantScriptUpdateTick]);

  const activeBrandingIdRef = useRef(activeBrandingId);
  activeBrandingIdRef.current = activeBrandingId;

  const prevActiveBrandingIdRef = useRef(activeBrandingId);
  useEffect(() => {
    if (prevActiveBrandingIdRef.current === activeBrandingId) {
      return;
    }

    prevActiveBrandingIdRef.current = activeBrandingId;
    sessionProgressRef.current = {};
    clearPrerenderedAudio();
    fetchScripts();
  }, [activeBrandingId]);

  useEffect(() => {
    log('current assistant progress state', progressState);
  }, [progressState]);

  const filteredChatHistory = useMemo(() => {
    if (!activeBrandingId) {
      return chatHistory;
    }
    return chatHistory.filter((m) => m.brandingId === activeBrandingId);
  }, [chatHistory, activeBrandingId]);

  const contextValue = useMemo(
    () => ({
      activeScreen,
      setActiveScreen,
      open,
      setOpen,
      handleSendRequest,
      chatHistory: filteredChatHistory,
      navigate,
      setNavigationHistory,
      awaitingResponseType,
      stopPlayback,
      isRecording,
      setIsRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow,
      simulateRegionChange,
      linearScriptPlayerRef,
      isAudioPlaybackActiveRef,
      isAudioPlaybackActive,
      scripts,
      isDisplayUserFeedback,
      setIsDisplayUserFeedback,
      handleSubmitUserFeedback,
      fetchScripts,
      fetchParticipantProgress,
      resetProgress,
      saveQuizAnswers,
      clearPrerenderedAudio,
      isScriptQuizActive,
      setIsScriptQuizActive,
      audioEndTick,
      isScriptCompleted,
      setIsScriptCompleted,
      isScriptTransitioning,
      canUserSkip,
      pendingScriptStart,
      confirmScriptStart,
      resetAssistantUI,
      userName: userName || '',
      useProfileLanguage: getEffectiveAiSettings().useProfileLanguage,
      availableLanguages:
        getEffectiveAiSettings().languages ?? defaultAvailableLanguages,
      audioLanguage,
      setAudioLanguage,
      isPollyAudioLoading,
    }),
    [
      activeScreen,
      open,
      handleSendRequest,
      filteredChatHistory,
      navigate,
      awaitingResponseType,
      stopPlayback,
      isRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow,
      simulateRegionChange,
      isAudioPlaybackActive,
      scripts,
      isDisplayUserFeedback,
      handleSubmitUserFeedback,
      fetchScripts,
      fetchParticipantProgress,
      resetProgress,
      saveQuizAnswers,
      clearPrerenderedAudio,
      isScriptQuizActive,
      audioEndTick,
      isScriptCompleted,
      isScriptTransitioning,
      canUserSkip,
      pendingScriptStart,
      confirmScriptStart,
      resetAssistantUI,
      userName,
      audioLanguage,
      isPollyAudioLoading,
    ],
  );

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = memo(AssistantProviderInner);
